<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { LANG_ENGLISH, LANG_FRENCH } from '@/constants';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import TopBannerPreview from './TopBannerPreview.vue';
import UploadMultipleImages from '../UploadMultipleImages.vue';

interface Props {
  payload: any;
  previewDataBottomBanner: any;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  'update:payload': [value: any];
  handleRemovePreviewImgBanner: [key: any, lang: string];
  preview: [value: any];
}>();

const { t } = useI18n({ useScope: 'global' });
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const multipleUploadEn = ref();
const multipleUploadFr = ref();
const visiblePreview = ref(false);
const headerPreviewBottomBanner = ref('');

const bottomBannerValue = computed({
  get() {
    return props.payload;
  },
  set(value) {
    emits('update:payload', value);
  }
});

const onUploadDataBottomBannerEn = (dataImage) => {
  bottomBannerValue.value.hPageEn.image = { ...dataImage };
  emits('update:payload', bottomBannerValue.value);
};

const onUploadDataBottomBannerFr = (dataImage) => {
  bottomBannerValue.value.hPageFr.image = { ...dataImage };
  emits('update:payload', bottomBannerValue.value);
};

const resetBottomMultipleUploadFile = () => {
  multipleUploadEn.value.resetFileUpload();
  multipleUploadFr.value.resetFileUpload();
};

const handleRemovePreviewImgBannerBottom = (key, lang: string) => {
  emits('handleRemovePreviewImgBanner', key, lang);
};

const openDialogPreviewBottomBanner = (bottomBannerValue, lang: string) => {
  emits('preview', bottomBannerValue);
  visiblePreview.value = true;
  if (lang === LANG_ENGLISH) {
    headerPreviewBottomBanner.value = t('promos.labels.preview_english');
    return;
  }
  headerPreviewBottomBanner.value = t('promos.labels.preview_french');
};

defineExpose({
  resetBottomMultipleUploadFile
});
</script>

<template>
  <Splitter layout="vertical">
    <SplitterPanel>
      <Splitter>
        <SplitterPanel class="p-3" :size="50">
          <h5 class="mb-5">{{ $t('promos.labels.english') }}</h5>
          <div class="flex flex-col">
            <span class="p-float-label mb-5">
              <InputText :disabled="!authorizations?.configurationPage?.promos_edit" id="bottom-linkEn" type="text" maxlength="200" class="mb-1 w-full" v-model.trim="bottomBannerValue.hPageEn.urlLink" />
              <label for="bottom-linkEn">{{ $t('promos.labels.url_link') }}</label>
            </span>
            <span class="p-float-label mb-5">
              <InputText :disabled="!authorizations?.configurationPage?.promos_edit" id="topbanner-imgTitleEn" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="bottomBannerValue.hPageEn.imageTitle" />
              <label for="topbanner-imgTitleEn">{{ $t('promos.labels.image_title') }}</label>
            </span>
            <div>
              <UploadMultipleImages
                ref="multipleUploadEn"
                :listUrlPreview="payload.hPageEn.image"
                @handleUploadData="onUploadDataBottomBannerEn"
                @handleRemoveImgReview="(key) => handleRemovePreviewImgBannerBottom(key, LANG_ENGLISH)"
              ></UploadMultipleImages>
            </div>
            <Button :label="$t('promos.labels.preview')" icon="pi pi-eye" class="mt-3" @click="openDialogPreviewBottomBanner(bottomBannerValue.hPageEn, LANG_ENGLISH)" />
          </div>
        </SplitterPanel>
        <SplitterPanel class="p-3" :size="50">
          <h5 class="mb-5">{{ $t('promos.labels.french') }}</h5>
          <div class="flex flex-col">
            <span class="p-float-label mb-5">
              <InputText :disabled="!authorizations?.configurationPage?.promos_edit" id="bottom-titleFr" type="text" maxlength="200" class="mb-1 w-full" v-model.trim="bottomBannerValue.hPageFr.urlLink" />
              <label for="bottom-titleFr">{{ $t('promos.labels.url_link') }}</label>
            </span>

            <span class="p-float-label mb-5">
              <InputText :disabled="!authorizations?.configurationPage?.promos_edit" id="bottom-imgTitleFr" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="bottomBannerValue.hPageFr.imageTitle" />
              <label for="bottom-imgTitleFr">{{ $t('promos.labels.image_title') }}</label>
            </span>
            <div>
              <UploadMultipleImages
                ref="multipleUploadFr"
                @handleUploadData="onUploadDataBottomBannerFr"
                :listUrlPreview="payload.hPageFr.image"
                @handleRemoveImgReview="(key) => handleRemovePreviewImgBannerBottom(key, LANG_FRENCH)"
              ></UploadMultipleImages>
            </div>

            <Button :label="$t('promos.labels.preview')" icon="pi pi-eye" class="mt-3" @click="openDialogPreviewBottomBanner(bottomBannerValue.hPageFr, LANG_FRENCH)" />
          </div>
        </SplitterPanel>
      </Splitter>
    </SplitterPanel>
  </Splitter>
  <Dialog :header="headerPreviewBottomBanner" class="w-7" v-model:visible="visiblePreview" :modal="true">
    <TopBannerPreview :value="previewDataBottomBanner"></TopBannerPreview>
  </Dialog>
</template>

<style lang="scss" scoped>
.upload-wrapper {
  width: 100%;
}
.item-upload {
  width: 100%;
}
.title-screen {
  font-weight: 700;
}
.p-button.p-component {
  padding: 0.45rem 0.95rem;
}
</style>
